import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Programmatic smooth scroll that respects the global Lenis instance.
export const smoothScrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
    } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

// Generic scroll-reveal wrapper.
export const Reveal = ({
    children,
    delay = 0,
    y = 40,
    once = true,
    className = "",
    ...props
}) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once, margin: "0px 0px -12% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: EASE, delay }}
            className={className}
            {...props}
        >
            {children}
        </motion.div>
    );
};

// Masked, line-by-line headline reveal. `lines` is an array of ReactNodes.
export const MaskReveal = ({
    lines,
    className = "",
    lineClassName = "",
    delay = 0,
    stagger = 0.12,
}) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
    return (
        <span ref={ref} className={className}>
            {lines.map((line, i) => (
                <span
                    key={`line-${i}`}
                    className="block overflow-hidden"
                    style={{ paddingBottom: "0.08em" }}
                >
                    <motion.span
                        className={`block ${lineClassName}`}
                        initial={{ y: "110%" }}
                        animate={inView ? { y: "0%" } : {}}
                        transition={{
                            duration: 1,
                            ease: EASE,
                            delay: delay + i * stagger,
                        }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </span>
    );
};

// Slow editorial marquee. `items` renders repeatedly.
export const Marquee = ({ items, speed = 38, className = "", separator }) => {
    const loop = [...items, ...items];
    return (
        <div className={`relative overflow-hidden ${className}`}>
            <motion.div
                className="flex whitespace-nowrap will-change-transform"
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: speed, ease: "linear", repeat: Infinity }}
            >
                {loop.map((item, i) => (
                    <span key={`marquee-${i}`} className="flex items-center shrink-0">
                        {item}
                        <span className="mx-8 opacity-40">{separator}</span>
                    </span>
                ))}
            </motion.div>
        </div>
    );
};
